import Api from 'api/base/axios.config';
import axios from 'axios';
import * as imageCompression from 'browser-image-compression';
import {
  GetPartTypeImageUploadUrlsPayload,
  GetPartTypeImageUploadUrlsResponse,
  GetPartTypeDimensionsPayload,
  GetPartTypeDimensionsResponse,
  GetAttributeValues,
  MasterPartTypeMappingResponse,
  PartAttribute,
  PartTypeBulkEditPayload,
  PartTypeBulkEditStatusResponse,
  PartTypeBulkEditSubmitResponse,
  PartTypeRemoveBackgroundPollResponse,
  PartTypeRemoveBackgroundPayload,
  PartTypeRemoveBackgroundResponse,
  PartTypePost,
  PutPartTypeDimensionsPayload,
  PartTypeUpdateRequestPayload,
  UpdateDeletedStatus,
  UpdatePartAttribute,
  PartTypeImageUploadTarget,
  PartTypeImageUploadUrlItem,
  UploadPartTypeImageToPresignedPayload,
  UploadPartTypeImageToPresignedResponse,
} from './types';

const BASE_URL = 'datamigrationparttype';
const PART_TYPE_IMAGE_UPLOAD_MAX_ATTEMPTS = 4;
const PART_TYPE_IMAGE_UPLOAD_RETRY_DELAY_MS = 1000;

const normalizeUploadString = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  if (!normalized || normalized.toLowerCase() === '[object object]') {
    return '';
  }

  return normalized;
};

const toUploadRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const normalizePartTypeImageUploadTarget = (
  value: unknown,
  fieldName: string,
): PartTypeImageUploadTarget => {
  const record = toUploadRecord(value);
  if (!record) {
    throw new Error(`Part type image upload response is malformed: ${fieldName} is missing.`);
  }

  const url = normalizeUploadString(record.url);
  if (!url) {
    throw new Error(`Part type image upload response is malformed: ${fieldName}.url is missing.`);
  }

  const fieldsRecord = toUploadRecord(record.fields);
  if (!fieldsRecord) {
    throw new Error(`Part type image upload response is malformed: ${fieldName}.fields is missing.`);
  }

  const fields = Object.entries(fieldsRecord).reduce<Record<string, string>>((nextFields, [key, fieldValue]) => {
    if (fieldValue === undefined || fieldValue === null) {
      return nextFields;
    }

    nextFields[key] = String(fieldValue);
    return nextFields;
  }, {});

  if (!normalizeUploadString(fields.key)) {
    throw new Error(`Part type image upload response is malformed: ${fieldName}.fields.key is missing.`);
  }

  return {
    url,
    fields,
  };
};

const normalizePartTypeImageUploadUrlItem = (value: unknown, index: number): PartTypeImageUploadUrlItem => {
  const record = toUploadRecord(value);
  if (!record) {
    throw new Error(`Part type image upload response is malformed: urls[${index}] is invalid.`);
  }

  const directTarget = record.url && record.fields ? record : null;
  const fullSizeSource = record.full_size_url || directTarget || record.web_url;
  const fullSizeUrl = normalizePartTypeImageUploadTarget(fullSizeSource, `urls[${index}].full_size_url`);
  const webUrl = record.web_url && record.full_size_url
    ? normalizePartTypeImageUploadTarget(record.web_url, `urls[${index}].web_url`)
    : undefined;

  return {
    web_url: webUrl,
    full_size_url: fullSizeUrl,
  };
};

const normalizeGetPartTypeImageUploadUrlsResponse = (value: unknown): GetPartTypeImageUploadUrlsResponse => {
  const record = toUploadRecord(value);
  if (!record) {
    throw new Error('Part type image upload response is malformed.');
  }

  const domain = normalizeUploadString(record.domain).replace(/\/+$/, '');
  if (!domain) {
    throw new Error('Part type image upload response is malformed: domain is missing.');
  }

  const directTarget = record.url && record.fields ? [record] : null;
  const urlItems = Array.isArray(record.urls) ? record.urls : directTarget;
  if (!Array.isArray(urlItems)) {
    throw new Error('Part type image upload response is malformed: urls is missing.');
  }

  const urls = urlItems.map((urlItem, index) => normalizePartTypeImageUploadUrlItem(urlItem, index));
  if (!urls.length) {
    throw new Error('Part type image upload response is malformed: urls is empty.');
  }

  return {
    domain,
    urls,
  };
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const getPresignedUploadRetryAfterMs = (error: unknown): number | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const retryAfter = error.response?.headers?.['retry-after'];
  const retryAfterValue = Array.isArray(retryAfter) ? retryAfter[0] : retryAfter;
  if (!retryAfterValue) {
    return null;
  }

  const retryAfterSeconds = Number(retryAfterValue);
  if (Number.isFinite(retryAfterSeconds)) {
    return Math.max(retryAfterSeconds * 1000, 0);
  }

  const retryAfterDateMs = Date.parse(String(retryAfterValue));
  if (Number.isFinite(retryAfterDateMs)) {
    return Math.max(retryAfterDateMs - Date.now(), 0);
  }

  return null;
};

const getPresignedUploadErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return '';
};

const getPresignedUploadResponseCode = (data: unknown): string => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return '';
  }

  const record = data as Record<string, unknown>;
  return String(record.Code || record.code || '').trim();
};

const isTransientPresignedUploadError = (error: unknown): boolean => {
  const errorMessage = getPresignedUploadErrorMessage(error).toLowerCase();

  if (axios.isAxiosError(error)) {
    const errorStatus = error.response?.status;
    const errorData = error.response?.data;
    const responseCode = getPresignedUploadResponseCode(errorData).toLowerCase();
    const responseText = typeof errorData === 'string' ? errorData.toLowerCase() : '';

    return (
      errorStatus === 503 ||
      (errorStatus ?? 0) >= 500 ||
      responseCode === 'slowdown' ||
      responseText.includes('slowdown') ||
      responseText.includes('slow down') ||
      errorMessage.includes('slowdown') ||
      errorMessage.includes('slow down') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('network') ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK'
    );
  }

  return (
    errorMessage.includes('request failed with status code 503') ||
    errorMessage.includes('slowdown') ||
    errorMessage.includes('slow down') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('network')
  );
};

const isSvgFile = (file: File): boolean => {
  return file.type.toLowerCase() === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
};

const getUploadContentType = (file: Blob | File): string => {
  if (file.type) {
    return file.type;
  }

  if (file instanceof File && isSvgFile(file)) {
    return 'image/svg+xml';
  }

  return 'application/octet-stream';
};

const getPresignedUploadFile = (
  file: Blob | File,
  fileName: string,
): { file: Blob | File; fileName: string } => {
  const uploadFileName = normalizeUploadString(file instanceof File ? file.name : fileName) || fileName;
  const uploadContentType = getUploadContentType(file);

  if (file instanceof File && file.type) {
    return {
      file,
      fileName: uploadFileName,
    };
  }

  return {
    file: new File([file], uploadFileName, { type: uploadContentType }),
    fileName: uploadFileName,
  };
};

const submitFileToPresignedUrlOnce = async (
  uploadTarget: PartTypeImageUploadTarget,
  file: Blob | File,
  fileName: string,
) => {
  const uploadFile = getPresignedUploadFile(file, fileName);
  const bodyFormData = new FormData();

  Object.entries(uploadTarget.fields).forEach(([key, fieldValue]) => {
    bodyFormData.append(key, fieldValue);
  });

  bodyFormData.append('file', uploadFile.file, uploadFile.fileName);

  await axios.post(uploadTarget.url, bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

const submitFileToPresignedUrl = async (
  uploadTarget: PartTypeImageUploadTarget,
  file: Blob | File,
  fileName: string,
) => {
  for (let attempt = 0; attempt < PART_TYPE_IMAGE_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
    try {
      await submitFileToPresignedUrlOnce(uploadTarget, file, fileName);
      return;
    } catch (error) {
      const hasMoreAttempts = attempt < PART_TYPE_IMAGE_UPLOAD_MAX_ATTEMPTS - 1;
      if (!hasMoreAttempts || !isTransientPresignedUploadError(error)) {
        throw error;
      }

      const retryAfterMs = getPresignedUploadRetryAfterMs(error);
      const retryDelayMs = retryAfterMs ?? PART_TYPE_IMAGE_UPLOAD_RETRY_DELAY_MS * Math.pow(2, attempt);
      await sleep(retryDelayMs);
    }
  }
};

const compressPartTypeImage = async (file: File) => {
  if (isSvgFile(file)) {
    return file;
  }

  return imageCompression.default(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });
};

const toAbsoluteMediaUrl = (value: string): string => {
  const normalizedValue = normalizeUploadString(value);
  if (!normalizedValue) {
    return '';
  }

  if (/^(https?:\/\/|data:|blob:)/i.test(normalizedValue)) {
    return normalizedValue;
  }

  if (normalizedValue.startsWith('//')) {
    return `https:${normalizedValue}`;
  }

  return `https://${normalizedValue.replace(/^\/+/, '')}`;
};

const resolveUploadObjectKey = (keyTemplate: string, fileName: string): string => {
  const normalizedKeyTemplate = normalizeUploadString(keyTemplate);
  if (!normalizedKeyTemplate) {
    return '';
  }

  const normalizedFileName = normalizeUploadString(fileName);
  if (!normalizedFileName) {
    return normalizedKeyTemplate.replace(/^\/+/, '');
  }

  return normalizedKeyTemplate
    .replace(/\$\{filename\}/gi, normalizedFileName)
    .replace(/^\/+/, '');
};

const buildUploadedMediaUrl = (domain: string, key: string): string => {
  const normalizedDomain = normalizeUploadString(domain).replace(/\/+$/, '');
  const normalizedKey = normalizeUploadString(key).replace(/^\/+/, '');

  if (!normalizedDomain || !normalizedKey) {
    return '';
  }

  if (/^(https?:\/\/|data:|blob:)/i.test(normalizedKey)) {
    return toAbsoluteMediaUrl(normalizedKey);
  }

  return toAbsoluteMediaUrl(`${normalizedDomain}/${normalizedKey}`);
};

// Get Part Types Auth
export const getPartTypesAuth = async () => {
  const partTypes = await Api.get(`${BASE_URL}/auth`);

  return partTypes.data;
};

export const getPartType = async (part_type_id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.set('part_type_id', part_type_id);

  const partTypes = await Api.get(`${BASE_URL}?${searchParams.toString()}`);

  return partTypes.data;
};

export const putPartType = async (payload: PartTypeUpdateRequestPayload | Record<string, unknown>) => {
  const response = await Api.put(`${BASE_URL}`, payload);
  return response.data;
};

export const postPartTypes = async (payload: PartTypePost) => {
  const options = await Api.post(`${BASE_URL}`, payload);
  return options.data;
};

export const postPartAttribute = async (payload: PartAttribute) => {
  const response = await Api.post(`${BASE_URL}/partTypeAttribute`, payload);
  return response.data;
};

export const updatePartAttribute = async (payload: UpdatePartAttribute) => {
  const options = await Api.put(`${BASE_URL}/partTypeAttribute`, payload);
  return options.data;
};

export const getPartAttributeValues = async (payload: GetAttributeValues) => {
  const options = await Api.post(`${BASE_URL}/attributeValues`, payload);
  return options.data;
};

export const updateDeletedStatus = async (payload: UpdateDeletedStatus) => {
  const options = await Api.put(`${BASE_URL}/updateDeletedStatus`, payload);
  return options.data;
};

export const getPartTypeDimensions = async (
  partTypeIds: string[] = [],
  tenantId: string | null = null,
): Promise<GetPartTypeDimensionsResponse> => {
  const validPartTypeIds = partTypeIds
    .map((partTypeId) => String(partTypeId || '').trim())
    .filter(Boolean);

  if (!validPartTypeIds.length) {
    return {};
  }

  const payload: GetPartTypeDimensionsPayload = {
    part_type_ids: validPartTypeIds,
    tenant_id: tenantId,
  };

  const response = await Api.post<GetPartTypeDimensionsResponse>(`${BASE_URL}/dimensions`, payload);
  return response.data;
};

export const putPartTypeDimensions = async (payload: PutPartTypeDimensionsPayload) => {
  const response = await Api.put(`${BASE_URL}/dimensions`, payload);
  return response.data;
};

export const postPartTypeBulkEdit = async (payload: PartTypeBulkEditPayload): Promise<PartTypeBulkEditSubmitResponse> => {
  const response = await Api.post(`${BASE_URL}/bulk-edit`, payload);
  return response.data;
};

export const getPartTypeBulkEditStatus = async (jobId: string): Promise<PartTypeBulkEditStatusResponse> => {
  const response = await Api.get(`${BASE_URL}/bulk-edit/${jobId}`);
  return response.data;
};

export const getPartTypeImageUploadUrls = async (
  payload: GetPartTypeImageUploadUrlsPayload,
): Promise<GetPartTypeImageUploadUrlsResponse> => {
  const partTypeId = String(payload.part_type_id || '').trim();
  if (!partTypeId) {
    throw new Error('Part type image upload request is missing part_type_id.');
  }

  const imageCount = Number(payload.image_count);
  if (!Number.isInteger(imageCount) || imageCount <= 0) {
    throw new Error('Part type image upload request is missing a valid image_count.');
  }

  const imageField = normalizeUploadString(payload.image_field);
  if (!imageField) {
    throw new Error('Part type image upload request is missing image_field.');
  }

  const searchParams = new URLSearchParams();
  searchParams.set('part_type_id', partTypeId);
  searchParams.set('image_field', imageField);
  searchParams.set('image_count', String(imageCount));

  const fileExtension = normalizeUploadString(payload.file_extension).replace(/^\./, '').toLowerCase();
  if (fileExtension) {
    searchParams.set('file_extension', fileExtension);
  }

  const contentType = normalizeUploadString(payload.content_type);
  if (contentType) {
    searchParams.set('content_type', contentType);
  }

  const tenantId = normalizeUploadString(payload.tenant_id);
  if (tenantId) {
    searchParams.set('tenant_id', tenantId);
  }

  const response = await Api.get(`${BASE_URL}/image?${searchParams.toString()}`);
  return normalizeGetPartTypeImageUploadUrlsResponse(response.data);
};

export const uploadPartTypeImageToPresignedUrl = async (
  payload: UploadPartTypeImageToPresignedPayload,
): Promise<UploadPartTypeImageToPresignedResponse> => {
  const normalizedDomain = normalizeUploadString(payload.domain).replace(/\/+$/, '');
  if (!normalizedDomain) {
    throw new Error('Part type image upload response is malformed: domain is missing.');
  }

  const isSvgUpload = isSvgFile(payload.file);
  let hasUploadedWebImage = false;

  if (!isSvgUpload && payload.presignedUrl.web_url) {
    const compressedImage = await compressPartTypeImage(payload.file);
    await submitFileToPresignedUrl(payload.presignedUrl.web_url, compressedImage, payload.file.name);
    hasUploadedWebImage = true;
  }
  await submitFileToPresignedUrl(payload.presignedUrl.full_size_url, payload.file, payload.file.name);

  const key = resolveUploadObjectKey(
    normalizeUploadString(payload.presignedUrl.full_size_url.fields.key),
    payload.file.name,
  );
  const webKey = !isSvgUpload
    ? resolveUploadObjectKey(
      normalizeUploadString(payload.presignedUrl.web_url?.fields.key),
      payload.file.name,
    )
    : '';
  if (!key) {
    throw new Error('Part type image upload response is malformed: full_size_url.fields.key is missing.');
  }

  const uploadedMediaUrl = buildUploadedMediaUrl(normalizedDomain, key);
  if (!uploadedMediaUrl) {
    throw new Error('Part type image upload response is malformed: unable to resolve uploaded image url.');
  }

  return {
    url: uploadedMediaUrl,
    web_url: hasUploadedWebImage && webKey ? buildUploadedMediaUrl(normalizedDomain, webKey) : undefined,
  };
};

export const postPartTypeRemoveBackground = async (
  payload: PartTypeRemoveBackgroundPayload,
): Promise<PartTypeRemoveBackgroundResponse> => {
  if (!Array.isArray(payload.images) || !payload.images.length) {
    throw new Error('Part type remove background request is missing images.');
  }

  const images = payload.images.map((imagePayload, index) => {
    const partTypeId = normalizeUploadString(imagePayload.part_type_id);
    if (!partTypeId) {
      throw new Error(`Part type remove background request is missing images[${index}].part_type_id.`);
    }

    const imageUrl = toAbsoluteMediaUrl(normalizeUploadString(imagePayload.image_url));
    if (!imageUrl) {
      throw new Error(`Part type remove background request is missing images[${index}].image_url.`);
    }

    const operation = normalizeUploadString(imagePayload.operation);
    if (!operation) {
      throw new Error(`Part type remove background request is missing images[${index}].operation.`);
    }

    return {
      part_type_id: partTypeId,
      image_url: imageUrl,
      operation,
    };
  });

  const requestPayload: {
    images: Array<{
      part_type_id: string;
      image_url: string;
      operation: string;
    }>;
    tenant_id?: string;
  } = {
    images,
  };

  const tenantId = normalizeUploadString(payload.tenant_id);
  if (tenantId) {
    requestPayload.tenant_id = tenantId;
  }

  const response = await Api.post(`${BASE_URL}/image/removeBackground`, requestPayload);
  return response.data;
};

export const getPartTypeRemoveBackgroundStatus = async (
  jobId: string,
): Promise<PartTypeRemoveBackgroundPollResponse> => {
  const normalizedJobId = normalizeUploadString(jobId);
  if (!normalizedJobId) {
    throw new Error('Part type remove background status request is missing job_id.');
  }

  const searchParams = new URLSearchParams();
  searchParams.set('job_id', normalizedJobId);

  const response = await Api.get(`${BASE_URL}/image/removeBackground?${searchParams.toString()}`);
  return response.data;
};

export const getMasterPartTypeMapping = async (tenantId: string): Promise<MasterPartTypeMappingResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set('tenant_id', tenantId);

  const response = await Api.get(`${BASE_URL}/master-mapping?${searchParams.toString()}`);
  return response.data;
};
